import type { IComponent } from "app/@types/ISwSettings";
import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import type { ICountryObject } from "app/services/CountryService";

class ActivationAssets {
    private getActivation() {
        return swSettings.components?.Activation;
    }
    private getActivationRankTracker() {
        return swSettings.components?.ActivationRankTracker;
    }
    private getRankTrackerComponent(): IComponent | undefined {
        return swSettings.components?.RankTracker;
    }
    get isAllowed(): boolean {
        return !!this.getActivation()?.isAllowed && !this.getActivation()?.isDisabled;
    }
    get availableCountries(): ICountryObject[] {
        return this.getActivation()?.allowedCountries || [];
    }
    get isMultiCountries(): boolean {
        return this.availableCountries.length > 1;
    }
    get isDisabled(): boolean {
        return Boolean(this.getActivation()?.isDisabled);
    }
    get activationSetsLimitation(): number {
        return this.getActivation()?.resources?.ActivationLimitUI;
    }
    get isMultiProjects(): boolean {
        return this.activationSetsLimitation > 1;
    }
    get isNewsAlertsAvailable(): boolean {
        return !!this.getActivation()?.resources?.HasAccessToNewslever;
    }
    get alertsSecondaryListEnabled(): boolean {
        return Boolean(this.getActivation()?.resources?.AlertsSecondaryListEnabled);
    }
    get alertsSecondaryListLimit(): number {
        return Number(this.getActivation()?.resources?.AlertsSecondaryListLimit);
    }
    get keywordsLimitation(): number {
        return this.getActivation()?.resources?.MaxAmountOfKeywords;
    }
    get competitorsLimit(): number {
        return this.getActivation()?.resources?.ActivationCompetitorsLimit;
    }
    get defaultCountry(): number {
        return this.getActivation()?.defaultParams?.country;
    }
    get defaultSource(): string {
        return this.getActivation()?.resources?.DefaultSource;
    }
    get isRankTrackerAvailable(): boolean {
        return Boolean(this.getActivationRankTracker()?.isAllowed && !this.getActivationRankTracker()?.isDisabled);
    }
    /**
     * True when Activation Rank Tracker UI is on and the user has the core Rank Tracker product
     * (not hook-only / hidden). Matches backend authorization for `api/activation/.../rankTracker/*`.
     */
    get isRankTrackerActivationApiEnabled(): boolean {
        if (!this.isRankTrackerAvailable) {
            return false;
        }
        const rankTracker = this.getRankTrackerComponent();
        if (!rankTracker?.isAllowed || rankTracker.isDisabled) {
            return false;
        }
        return isAvailable(rankTracker);
    }
    get isAiCompetitorsEnabled(): boolean {
        return Boolean(this.getActivation()?.resources?.AiCompetitorsEnabled);
    }
    get maxAmountOfKeywords(): number {
        return Number(this.getActivation()?.resources?.MaxAmountOfKeywords);
    }

    get projectsLimit(): number {
        return this.getActivation()?.resources?.ActivationLimitUI ?? 0;
    }
    get isHomepage2Available(): boolean {
        return Boolean(this.getActivation()?.resources?.["ActivationHomepage2.0"]);
    }
}

export const ActivationComponentAssets = new ActivationAssets();
